import type { SetStateAction, Dispatch } from 'react';
import React, { createContext, useContext, useState, useEffect } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocalStorage } from '@uidotdev/usehooks';

import { useTheme } from '../../../styles';

export interface NavigationContextValue {
  routePrefix: string;
  isNavSnapExpanded: boolean;
  isNavOverlayExpanded: boolean;
  isNavExpanded: boolean;
  toggleNavigation: () => void;
  handleNavMenuClick: (
    openCollapsibleMenu: boolean,
    setOpenCollapsibleMenu: Dispatch<SetStateAction<boolean>>,
  ) => void;
  closeOverlayNavigation: () => void;
  handleNavLinkClick: () => void;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

export const useNavigation = (): NavigationContextValue => {
  const value = useContext(NavigationContext);

  if (value === null) {
    throw new Error(
      'Fenrir: useNavigation must be used within the context of NavigationProvider.',
    );
  }

  return value;
};

export function NavigationProvider({
  children,
  routePrefix,
}: React.PropsWithChildren<{ routePrefix: string }>) {
  const theme = useTheme();
  const isOverLgViewport = useMediaQuery(theme.breakpoints.up('lg'));
  const [savedNavSnapExpandedState, setNavSnapExpandedState] =
    useLocalStorage<boolean>('awDashboardNavExpended', true);

  const [isNavSnapExpanded, setIsNavSnapExpanded] = useState(
    isOverLgViewport ? savedNavSnapExpandedState : false,
  );
  const [isNavOverlayExpanded, setIsNavOverlayExpanded] = useState(false);

  const isNavExpanded = isNavSnapExpanded || isNavOverlayExpanded;

  const toggleNavigation = () => {
    if (isOverLgViewport) {
      setIsNavSnapExpanded(!isNavSnapExpanded);
      setNavSnapExpandedState(!isNavSnapExpanded);
      setIsNavOverlayExpanded(false);
    } else {
      setIsNavOverlayExpanded(!isNavOverlayExpanded);
      setIsNavSnapExpanded(false);
    }
  };

  const closeOverlayNavigation = () => {
    if (!isNavSnapExpanded) {
      setIsNavOverlayExpanded(false);
    }
  };

  const handleNavMenuClick = (
    openCollapsibleMenu: boolean,
    setOpenCollapsibleMenu: Dispatch<SetStateAction<boolean>>,
  ) => {
    if (!isNavExpanded) {
      setOpenCollapsibleMenu(true);
      setIsNavOverlayExpanded(true);
    } else {
      setOpenCollapsibleMenu(!openCollapsibleMenu);
    }
  };

  const handleNavLinkClick = () => {
    if (isNavOverlayExpanded) {
      setIsNavOverlayExpanded(false);
    }
  };

  useEffect(() => {
    if (!isOverLgViewport && isNavSnapExpanded) {
      setIsNavSnapExpanded(false);
    }
  }, [isOverLgViewport, isNavSnapExpanded]);

  return (
    <NavigationContext.Provider
      value={{
        routePrefix,
        isNavSnapExpanded,
        isNavOverlayExpanded,
        isNavExpanded,
        toggleNavigation,
        handleNavMenuClick,
        closeOverlayNavigation,
        handleNavLinkClick,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}
